import { z } from 'zod';
/**
 * List of valid Home Assistant weekday strings.
 * Used for time-based conditions and triggers.
 */
export const VALID_WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
/**
 * Zod schema for Home Assistant condition objects.
 * Supports recursive conditions for and/or/not groups.
 */
export const HAConditionSchema = z.looseObject({
    alias: z.string().optional(),
    condition: z.string().optional(),
    enabled: z.boolean().optional(),
    entity_id: z.union([z.string(), z.array(z.string())]).optional(),
    state: z.union([z.string(), z.array(z.string())]).optional(),
    value_template: z.string().optional(),
    after: z.string().optional(),
    before: z.string().optional(),
    weekday: z.array(z.enum(VALID_WEEKDAYS)).optional(),
    after_offset: z.string().optional(),
    before_offset: z.string().optional(),
    zone: z.string().optional(),
    conditions: z.array(z.lazy(() => HAConditionSchema)).optional(),
    above: z.union([z.string(), z.number()]).optional(),
    below: z.union([z.string(), z.number()]).optional(),
    attribute: z.string().optional(),
    // Support both string and array for trigger conditions
    id: z.union([z.string(), z.array(z.string())]).optional(),
});
/**
 * Enum of valid Home Assistant trigger platforms.
 */
export const HAPlatformEnum = z.enum([
    'event',
    'template',
    'zone',
    'state',
    'time',
    'time_pattern',
    'mqtt',
    'webhook',
    'sun',
    'numeric_state',
    'homeassistant',
    'device',
    'calendar',
]);
/**
 * One id or a list of them, which is how Home Assistant writes every target key.
 */
export const HAIdOrIdsSchema = z.union([z.string(), z.array(z.string())]);
/**
 * Zod schema for a Home Assistant target selector.
 * Every key is optional: a target may name entities, devices, labels, areas or
 * floors, in any combination, and triggers that take a target accept the same
 * shape as actions do.
 */
export const HATargetSchema = z.looseObject({
    entity_id: HAIdOrIdsSchema.optional(),
    device_id: HAIdOrIdsSchema.optional(),
    label_id: HAIdOrIdsSchema.optional(),
    area_id: HAIdOrIdsSchema.optional(),
    floor_id: HAIdOrIdsSchema.optional(),
});
/**
 * Zod schema for Home Assistant trigger objects.
 * Normalizes both legacy 'platform' and modern 'trigger' fields to a single 'trigger' property.
 * Supports both legacy format (platform: state) and modern format (trigger: state).
 */
export const HATriggerSchema = z
    .looseObject({
    alias: z.string().optional(),
    platform: z.string().optional(),
    trigger: z.string().optional(),
    target: HATargetSchema.optional(),
    options: z.looseObject({}).optional(),
    entity_id: HAIdOrIdsSchema.optional(),
    // Home Assistant supports both string, array, and null for from/to fields
    from: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
    to: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
    for: z
        .union([
        z.string(),
        z.number(),
        z.object({
            hours: z.union([z.number(), z.string()]).optional(),
            minutes: z.union([z.number(), z.string()]).optional(),
            seconds: z.union([z.number(), z.string()]).optional(),
            milliseconds: z.union([z.number(), z.string()]).optional(),
        }),
    ])
        .optional(),
    at: z.unknown().optional(),
    offset: z.string().optional(),
    event: z.string().optional(),
    event_type: z.union([z.string(), z.array(z.string())]).optional(),
    event_data: z.record(z.string(), z.unknown()).optional(),
    above: z.union([z.string(), z.number()]).optional(),
    below: z.union([z.string(), z.number()]).optional(),
    value_template: z.string().optional(),
    template: z.string().optional(),
    webhook_id: z.string().optional(),
    zone: z.string().optional(),
    topic: z.string().optional(),
    payload: z.string().optional(),
    // Conversation trigger fields
    command: z.union([z.string(), z.array(z.string())]).optional(),
})
    .transform((input) => {
    // Normalize to modern 'trigger' property (HA 2024.1+)
    const trigger = input.trigger ?? input.platform ?? 'state';
    // Remove the legacy 'platform' key
    const { platform: _platform, ...rest } = input;
    return {
        ...rest,
        trigger,
    };
});
/**
 * Zod schema for FlowGraph metadata block (automation-level settings)
 */
export const FlowGraphMetadataSchema = z.object({
    mode: z.enum(['single', 'restart', 'queued', 'parallel']).default('single'),
    max: z.number().optional(),
    max_exceeded: z.enum(['silent', 'warning', 'critical']).optional(),
    initial_state: z.boolean().optional(),
    hide_entity: z.boolean().optional(),
    trace: z.object({ stored_traces: z.number().optional() }).optional(),
});
/**
 * Type guard for Home Assistant trigger objects.
 * Returns true if the object matches the HATrigger shape.
 */
export function isHATrigger(obj) {
    return (typeof obj === 'object' &&
        obj !== null &&
        ('platform' in obj || 'trigger' in obj || 'entity_id' in obj));
}
/**
 * Type guard for Home Assistant condition objects.
 * Returns true if the object matches the HACondition shape.
 */
export function isHACondition(obj) {
    return (typeof obj === 'object' &&
        obj !== null &&
        ('condition' in obj || 'entity_id' in obj || 'state' in obj));
}
/**
 * Type guard for Home Assistant device actions.
 * Returns true if the object has type, device_id, and domain fields.
 */
export function isDeviceAction(obj) {
    return (typeof obj === 'object' &&
        obj !== null &&
        'type' in obj &&
        'device_id' in obj &&
        'domain' in obj);
}
/**
 * C.A.F.E. metadata stored in automation YAML to preserve flow layout.
 */
export const CafeMetadataSchema = z.object({
    version: z.number(),
    nodes: z.record(z.string(), z.object({
        x: z.number(),
        y: z.number(),
    })),
    graph_id: z.string(),
    graph_version: z.number(),
    strategy: z.enum(['native', 'state-machine']),
});
/**
 * Type guard for C.A.F.E. metadata.
 */
export function isCafeMetadata(obj) {
    return CafeMetadataSchema.safeParse(obj).success;
}
/**
 * Zod schema for choose option in HA actions.
 */
export const HAChooseOptionSchema = z.lazy(() => z.object({
    conditions: z.union([HAConditionSchema, z.array(HAConditionSchema)]),
    sequence: z.union([HAActionSchema, z.array(HAActionSchema)]),
    alias: z.string().optional(),
}));
/**
 * Zod schema for Home Assistant action objects.
 */
export const HAActionSchema = z.lazy(() => z.looseObject({
    service: z.string().optional(),
    action: z.string().optional(),
    event: z.string().optional(),
    event_data: z.record(z.string(), z.unknown()).optional(),
    id: z.string().optional(),
    alias: z.string().optional(),
    target: z.record(z.string(), z.unknown()).optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    data_template: z.record(z.string(), z.unknown()).optional(),
    response_variable: z.string().optional(),
    continue_on_error: z.boolean().optional(),
    enabled: z.boolean().optional(),
    delay: z.union([z.string(), z.number(), z.record(z.string(), z.number())]).optional(),
    wait_template: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
    timeout: z.union([z.string(), z.number(), z.record(z.string(), z.number())]).optional(),
    continue_on_timeout: z.boolean().optional(),
    wait_for_trigger: z.union([HATriggerSchema, z.array(HATriggerSchema)]).optional(),
    choose: z.union([HAChooseOptionSchema, z.array(HAChooseOptionSchema)]).optional(),
    default: z.array(HAActionSchema).optional(),
    if: z.array(HAConditionSchema).optional(),
    then: z.array(HAActionSchema).optional(),
    else: z.array(HAActionSchema).optional(),
    variables: z.record(z.string(), z.unknown()).optional(),
    repeat: z
        .object({
        count: z.union([z.string(), z.number()]).optional(),
        while: z.array(HAConditionSchema).optional(),
        until: z.union([z.string(), z.array(z.string()), z.array(HAConditionSchema)]).optional(),
        sequence: z.array(HAActionSchema),
    })
        .optional(),
}));
/**
 * Zod schema for a full Home Assistant automation.
 */
export const HAAutomationSchema = z.object({
    id: z.string().optional(),
    alias: z.string().optional(),
    description: z.string().optional(),
    trigger_variables: z.record(z.string(), z.unknown()).optional(),
    trigger: z.union([HATriggerSchema, z.array(HATriggerSchema)]).optional(),
    condition: z.union([HAConditionSchema, z.array(HAConditionSchema)]).optional(),
    action: z.union([HAActionSchema, z.array(HAActionSchema)]),
    mode: z.enum(['single', 'restart', 'queued', 'parallel']).optional().default('single'),
    max: z.number().optional(),
    max_exceeded: z.enum(['silent', 'warning']).optional(),
    initial_state: z.boolean().optional(),
    hide_entity: z.boolean().optional(),
    trace: z.record(z.string(), z.unknown()).optional(),
    variables: z
        .object({
        _cafe_metadata: CafeMetadataSchema.optional(),
    })
        .catchall(z.unknown())
        .optional(),
});
/**
 * Zod schema for a Home Assistant script.
 */
export const HAScriptSchema = HAAutomationSchema.omit({ action: true }).extend({
    action: z.union([HAActionSchema, z.array(HAActionSchema)]).optional(),
    sequence: z.union([HAActionSchema, z.array(HAActionSchema)]),
});
/**
 * Zod schema for Home Assistant delay action.
 */
export const HADelaySchema = z.looseObject({
    id: z.string().optional(),
    alias: z.string().optional(),
    delay: z.union([
        z.string(),
        z.looseObject({
            hours: z.union([z.number(), z.string()]).optional(),
            minutes: z.union([z.number(), z.string()]).optional(),
            seconds: z.union([z.number(), z.string()]).optional(),
            milliseconds: z.union([z.number(), z.string()]).optional(),
        }),
    ]),
});
/**
 * Zod schema for Home Assistant wait action (wait_template or wait_for_trigger).
 */
export const HAWaitSchema = z
    .looseObject({
    id: z.string().optional(),
    alias: z.string().optional(),
    wait_template: z.string().optional(),
    wait_for_trigger: z.array(HATriggerSchema).optional(),
    timeout: z
        .union([
        z.string(),
        z.looseObject({
            hours: z.union([z.number(), z.string()]).optional(),
            minutes: z.union([z.number(), z.string()]).optional(),
            seconds: z.union([z.number(), z.string()]).optional(),
            milliseconds: z.union([z.number(), z.string()]).optional(),
        }),
    ])
        .optional(),
    continue_on_timeout: z.boolean().optional(),
})
    .refine((data) => {
    return data.wait_template === undefined || data.wait_for_trigger === undefined;
}, {
    message: 'Provide either `wait_template` or `wait_for_trigger`, but not both.',
    path: ['wait_template'],
});
/**
 * Zod schema for Home Assistant variables action.
 */
export const HAVariablesSchema = z.looseObject({
    id: z.string().optional(),
    alias: z.string().optional(),
    variables: z.record(z.string(), z.unknown()),
});
//# sourceMappingURL=ha-schemas.js.map